/**
 * Deals Hero Section - Gradient hero with stats, Rumio balance, and spin button
 */

import { Search, Tag, TrendingDown, Package, Clock, Users } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useWebsiteContentOptional } from '../../../../contexts/WebsiteContentContext';
import { useAuthStore } from '../../../../stores/auth-store';
import EditableElement from '../../../../components/admin/EditableElement';
import { RumioBalanceInline } from './RumioBalance';
import { SlotMachine3DCompact } from './SlotMachine3DCompact';
import type { DealsStats } from '../types';

interface Props {
  stats?: DealsStats;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onSpinClick: () => void;
  onPurchaseClick: () => void;
  onLoginRequired: () => void;
}

export default function DealsHero({
  stats,
  searchValue,
  onSearchChange,
  onSpinClick,
  onPurchaseClick,
  onLoginRequired,
}: Props) {
  const { t } = useLanguage();
  const content = useWebsiteContentOptional();
  const { user } = useAuthStore();

  // Helper to get content with fallback (CMS > i18n > hardcoded)
  const get = (key: string, fallback: string, i18nKey?: string) => {
    const translatedFallback = i18nKey ? t(i18nKey, fallback) : fallback;
    return content?.getContent('deals_hero', key, translatedFallback) ?? translatedFallback;
  };

  return (
    <section className="relative overflow-hidden">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800" />

      {/* Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary-500/30 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-primary-400/20 rounded-full blur-3xl" />
      </div>

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 lg:pt-16 pb-32">
        {/* Top Row - Badge + Rumio Balance + Spin */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          {/* Badge */}
          <EditableElement section="deals_hero" field="badge">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-white/20 rounded-full text-white text-sm font-medium backdrop-blur-sm">
              <Tag className="w-4 h-4" />
              {get('badge', 'Browse Deals', 'dealsHero.badge')}
            </div>
          </EditableElement>

          {/* User Actions */}
          {user && (
            <div className="flex items-center gap-3">
              <RumioBalanceInline onClick={onPurchaseClick} />
              <SlotMachine3DCompact onSpin={onSpinClick} onLoginRequired={onLoginRequired} />
            </div>
          )}
        </div>

        {/* Title */}
        <EditableElement section="deals_hero" field="title">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            {get('title', 'Find your', 'dealsHero.title')}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-orange-300">
              {get('title_highlight', 'deals', 'dealsHero.titleHighlight')}
            </span>
          </h1>
        </EditableElement>

        {/* Subtitle */}
        <EditableElement section="deals_hero" field="subtitle">
          <p className="text-white/80 text-lg max-w-2xl mb-8">
            {get('subtitle', 'Discover exclusive deals on IKEA products. In-store As-Is finds, family offers, and limited-time specials.', 'dealsHero.subtitle')}
          </p>
        </EditableElement>

        {/* Search Bar */}
        <div className="max-w-xl">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={t('dealsHero.searchPlaceholder', 'Search products, categories...')}
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full pl-12 pr-4 py-4 bg-white rounded-xl text-gray-900 placeholder-gray-400 shadow-lg focus:outline-none focus:ring-4 focus:ring-white/20 transition-all"
            />
          </div>
        </div>

        {/* Stats Row */}
        {stats && (
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
            <StatCard
              icon={<TrendingDown className="w-5 h-5" />}
              value={stats.totalDeals}
              label={t('dealsHero.stats.total', 'Total Deals')}
            />
            <StatCard
              icon={<Package className="w-5 h-5" />}
              value={stats.mysteryBoxes}
              label={t('dealsHero.stats.mystery', 'Mystery Boxes')}
              highlight
            />
            <StatCard
              icon={<Clock className="w-5 h-5" />}
              value={stats.lastChance}
              label={t('dealsHero.stats.lastChance', 'Last Chance')}
            />
            <StatCard
              icon={<Users className="w-5 h-5" />}
              value={stats.familyOffers}
              label={t('dealsHero.stats.family', 'Family Offers')}
            />
            <StatCard
              icon={<Tag className="w-5 h-5" />}
              value={stats.limitedTime}
              label={t('dealsHero.stats.limited', 'Limited Time')}
              className="hidden sm:flex"
            />
            <StatCard
              icon={<Package className="w-5 h-5" />}
              value={stats.asIs}
              label={t('dealsHero.stats.asIs', 'As-Is')}
              className="hidden lg:flex"
            />
          </div>
        )}
      </div>

      {/* Wave Divider */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 80" fill="none" className="w-full h-auto">
          <path
            d="M0 80L60 70C120 60 240 40 360 35C480 30 600 40 720 45C840 50 960 50 1080 45C1200 40 1320 30 1380 25L1440 20V80H1380C1320 80 1200 80 1080 80C960 80 840 80 720 80C600 80 480 80 360 80C240 80 120 80 60 80H0Z"
            fill="white"
          />
        </svg>
      </div>
    </section>
  );
}

function StatCard({
  icon,
  value,
  label,
  highlight,
  className = '',
}: {
  icon: React.ReactNode;
  value: number;
  label: string;
  highlight?: boolean;
  className?: string;
}) {
  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 rounded-xl backdrop-blur-sm ${
        highlight
          ? 'bg-gradient-to-r from-amber-500/30 to-orange-500/30 border border-amber-400/30'
          : 'bg-white/10 border border-white/10'
      } ${className}`}
    >
      <div className={`${highlight ? 'text-amber-300' : 'text-white/70'}`}>{icon}</div>
      <div>
        <p className="text-xl font-bold text-white">{value.toLocaleString()}</p>
        <p className="text-xs text-white/60">{label}</p>
      </div>
    </div>
  );
}
